<template>
  <div class="compare">
    <div class="grid">
      <div v-for="sc in scenarios" :key="sc.key" class="cell" :class="{ current: sc.current }">
        <div class="cell-title">{{ sc.name }}<span v-if="sc.current" class="live-tag">live</span></div>
        <svg :viewBox="`0 0 ${W} ${H}`" class="mini">
          <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(0.65)" :y2="y(0.65)" class="benchmark" />
          <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(0.70)" :y2="y(0.70)" class="ceiling" />
          <path :d="path(sc.results)" class="series" fill="none" />
          <circle
            :cx="x(sc.results, 2027)" :cy="y(r27(sc).staffPct)" r="3" class="dot"
          />
          <text :x="PAD.l" :y="y(0.65) - 4" class="ref">65%</text>
          <text :x="PAD.l" :y="y(0.70) - 4" class="ref">70%</text>
        </svg>
        <table class="facts">
          <tbody>
            <tr><td>Staff % 2027</td><td>{{ (r27(sc).staffPct * 100).toFixed(1) }}%</td></tr>
            <tr><td>Result 2027</td><td>{{ fmtM(r27(sc).operatingResult) }}</td></tr>
            <tr><td>Cumulative to {{ lastYear(sc) }}</td><td>{{ fmtM(cumulative(sc)) }}</td></tr>
            <tr><td>Cash months {{ lastYear(sc) }}</td><td>{{ lastRow(sc).monthsCash ? lastRow(sc).monthsCash.toFixed(1) : '—' }}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
    <p class="note">Identical scales on every panel; the dot marks 2027. Saved scenarios keep the lever settings they were saved with.</p>
  </div>
</template>

<script>
import { scaleLinear, linePath } from '../../utils/chartUtils';
import { formatM } from '../../utils/boardEngine';

const DOMAIN = [0.56, 0.84];

export default {
  name: 'ScenarioCompare',
  // scenarios: [{ key, name, results, current? }]
  props: { scenarios: { type: Array, required: true } },
  data: () => ({ W: 300, H: 120, PAD: { l: 10, r: 10, t: 10, b: 8 } }),
  computed: {
    y() { return scaleLinear(DOMAIN, [this.H - this.PAD.b, this.PAD.t]); },
  },
  methods: {
    fmtM: formatM,
    xScale(results) {
      const years = results.map((r) => r.year);
      return scaleLinear([Math.min(...years), Math.max(...years)], [this.PAD.l + 6, this.W - this.PAD.r - 6]);
    },
    x(results, year) { return this.xScale(results)(year); },
    path(results) {
      const xs = this.xScale(results);
      return linePath(results.map((r) => [xs(r.year), this.y(r.staffPct)]));
    },
    r27(sc) { return sc.results.find((r) => r.year === 2027); },
    lastRow(sc) { return sc.results[sc.results.length - 1]; },
    lastYear(sc) { return this.lastRow(sc).year; },
    cumulative(sc) {
      return sc.results.filter((r) => r.year >= 2027).reduce((s, r) => s + r.operatingResult, 0);
    },
  },
};
</script>

<style scoped>
.grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 18px; }
.cell { border: 1px solid var(--hairline); padding: 10px 12px; }
.cell.current { border-color: var(--ink); }
.cell-title { font-size: 12px; font-weight: 700; color: var(--ink); margin-bottom: 6px; }
.live-tag {
  margin-left: 8px; font-size: 9px; font-weight: 700; letter-spacing: 0.1em;
  color: var(--red); text-transform: uppercase;
}
.mini { width: 100%; height: auto; display: block; }
.benchmark { stroke: var(--ink); stroke-width: 1; }
.ceiling { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 4 3; }
.series { stroke: var(--red); stroke-width: 1.8; stroke-linejoin: round; }
.dot { fill: var(--red); stroke: var(--paper); stroke-width: 1.5; }
.ref { font-size: 8.5px; fill: var(--muted); }
.facts { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11.5px; }
.facts td { padding: 3px 0; border-bottom: 1px solid var(--hairline); color: var(--ink); }
.facts td:last-child { text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; }
.note { font-size: 10.5px; color: var(--muted); margin: 12px 0 0; }
</style>
